/* RingingCallView — Scan2Call compact incoming-call card for the resident.
   -----------------------------------------------------------------------
   Extracted from `DoorbellRingDialog.js` (Feb 2026). Kept as a
   centred dialog (not fullscreen) because the resident hasn't answered
   yet — a fullscreen banner would be jarring while they're on other
   screens.
*/
import { PhoneCall, PhoneOff, Loader2, MapPin, ExternalLink } from "lucide-react";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogTitle } from "../ui/dialog";
import { visitorMapsUrl } from "./visitorMapsUrl";

export default function RingingCallView({ ring, stage, onAnswer, onDecline, onHangup }) {
  const mapsUrl = visitorMapsUrl(ring);
  return (
    <Dialog open={true} onOpenChange={(o) => { if (!o) onHangup(); }}>
      <DialogContent
        className="sm:max-w-md rounded-sm p-0 overflow-hidden bg-gray-900 text-white border-gray-800"
        data-testid="doorbell-ring-dialog"
      >
        <DialogTitle className="sr-only">
          Scan2Call from {ring.visitor_name || "Visitor"}
        </DialogTitle>
        <div className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-widest font-bold text-emerald-400 font-body">Scan2Call</p>
              {ring.visitor_name && (
                <p className="font-heading text-xl font-black tracking-tighter mt-0.5" data-testid="ring-visitor-name">
                  {ring.visitor_name}
                </p>
              )}
              {mapsUrl && (
                <a
                  href={mapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-xs text-gray-400 font-body mt-1 truncate hover:text-emerald-300 underline decoration-dotted underline-offset-2 transition-colors"
                  title="Open visitor's location on Google Maps"
                  data-testid="ring-visitor-location"
                >
                  <MapPin className="w-3 h-3 shrink-0 text-emerald-500" />
                  <span className="truncate">
                    Calling from {ring.visitor_location || "visitor's GPS location"}
                  </span>
                  <ExternalLink className="w-2.5 h-2.5 shrink-0 opacity-70" />
                </a>
              )}
            </div>
          </div>

          {/* Placeholder while ringing/connecting — no video yet */}
          <div
            className="relative w-full aspect-video bg-black rounded-sm overflow-hidden border border-gray-800"
            data-testid="ring-visitor-placeholder"
          >
            <div className="absolute inset-0 flex items-center justify-center">
              {stage === "connecting" ? (
                <Loader2 className="w-8 h-8 animate-spin text-emerald-400" />
              ) : (
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-500/20 border-2 border-emerald-500 animate-pulse mb-2">
                    <PhoneCall className="w-7 h-7 text-emerald-400" />
                  </div>
                  <p className="text-[10px] uppercase tracking-widest text-gray-400 font-body">Incoming</p>
                </div>
              )}
            </div>
          </div>

          {stage === "ringing" && (
            <div className="grid grid-cols-2 gap-2 mt-4">
              <Button
                onClick={onDecline}
                variant="outline"
                className="border-red-500/50 text-red-400 hover:bg-red-500/10 rounded-sm font-body"
                data-testid="ring-decline-btn"
              >
                <PhoneOff className="w-4 h-4 mr-1.5" /> Decline
              </Button>
              <Button
                onClick={onAnswer}
                className="bg-emerald-500 hover:bg-emerald-600 text-black font-body font-bold rounded-sm"
                data-testid="ring-answer-btn"
              >
                <PhoneCall className="w-4 h-4 mr-1.5" /> Answer
              </Button>
            </div>
          )}

          <p className="text-[10px] text-gray-500 font-body text-center mt-3">
            {stage === "ringing"
              ? "Video call — the visitor will see and hear you when you answer."
              : "Connecting…"}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
